<template>
  <div class="tw-h-full tw-flex tw-flex-col tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-overflow-hidden">

    <!-- Header -->
    <div class="tw-px-4 tw-pt-4 tw-pb-3 tw-border-b tw-border-gray-100">
      <div class="tw-flex tw-items-center tw-justify-between tw-mb-3">
        <h2 class="tw-text-base tw-font-semibold tw-text-gray-900">Messages</h2>
        <button
          @click="refreshConversations"
          :disabled="loading"
          class="tw-p-1.5 tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-gray-100 tw-rounded-lg tw-transition-all"
          :class="{ 'tw-animate-spin': loading }"
          title="Refresh"
        >
          <icon icon="solar:refresh-bold-duotone" class="tw-text-base" />
        </button>
      </div>

      <!-- Search -->
      <div class="tw-relative">
        <icon icon="solar:magnifer-bold-duotone" class="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 tw-text-sm tw-pointer-events-none" />
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search conversations..."
          class="tw-w-full tw-pl-9 tw-pr-4 tw-py-2 tw-text-sm tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-outline-none focus:tw-bg-white focus:tw-border-primary-400 focus:tw-ring-2 focus:tw-ring-primary-100 tw-transition-all"
        />
        <button
          v-if="searchQuery"
          @click="searchQuery = ''"
          class="tw-absolute tw-right-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600"
        >
          <icon icon="solar:close-circle-bold" class="tw-text-sm" />
        </button>
      </div>
    </div>

    <!-- Type Tabs -->
    <div class="tw-flex tw-gap-1 tw-px-3 tw-py-2 tw-bg-gray-50 tw-border-b tw-border-gray-100">
      <button
        v-for="tab in tabs"
        :key="tab.type"
        @click="setActiveType(tab.type)"
        :class="[
          'tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-rounded-lg tw-transition-all tw-flex-1 tw-justify-center',
          activeType === tab.type
            ? 'tw-bg-white tw-text-primary-600 tw-shadow-sm tw-border tw-border-gray-200'
            : 'tw-text-gray-500 hover:tw-text-gray-700 hover:tw-bg-white/60'
        ]"
      >
        <icon :icon="tab.icon" class="tw-text-sm" />
        {{ tab.label }}
        <span
          v-if="getTabUnreadCount(tab.type) > 0"
          class="tw-bg-primary-500 tw-text-white tw-text-[10px] tw-font-bold tw-px-1.5 tw-py-0.5 tw-rounded-full tw-leading-none"
        >
          {{ getTabUnreadCount(tab.type) > 99 ? '99+' : getTabUnreadCount(tab.type) }}
        </span>
      </button>
    </div>

    <!-- Loading Skeletons -->
    <div v-if="loading" class="tw-flex-1 tw-p-3 tw-space-y-1 tw-overflow-hidden">
      <div v-for="i in 6" :key="i" class="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-rounded-xl">
        <div class="tw-w-11 tw-h-11 tw-bg-gray-100 tw-rounded-full tw-animate-pulse tw-flex-shrink-0"></div>
        <div class="tw-flex-1 tw-space-y-2">
          <div class="tw-h-3.5 tw-bg-gray-100 tw-rounded-md tw-w-28 tw-animate-pulse"></div>
          <div class="tw-h-3 tw-bg-gray-100 tw-rounded-md tw-w-44 tw-animate-pulse"></div>
        </div>
        <div class="tw-h-3 tw-bg-gray-100 tw-rounded tw-w-10 tw-animate-pulse"></div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="filteredConversations.length === 0"
      class="tw-flex-1 tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-6 tw-text-center"
    >
      <div class="tw-w-12 tw-h-12 tw-bg-gray-100 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-mb-3">
        <icon
          :icon="searchQuery ? 'solar:magnifer-bold-duotone' : (activeType === 'company' ? 'solar:buildings-2-bold-duotone' : 'solar:chat-round-dots-bold-duotone')"
          class="tw-text-xl tw-text-gray-400"
        />
      </div>
      <p class="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
        {{ searchQuery ? 'No results found' : 'No conversations yet' }}
      </p>
      <p class="tw-text-xs tw-text-gray-400 tw-max-w-[200px]">
        {{
          searchQuery
            ? `No conversations match "${searchQuery}"`
            : activeType === 'company'
              ? 'Chat with a company from their profile page.'
              : 'Connect with other users to start chatting.'
        }}
      </p>
    </div>

    <!-- Conversations List -->
    <div v-else class="tw-flex-1 tw-overflow-y-auto tw-p-2 conversations-scroll">
      <div
        v-for="conversation in filteredConversations"
        :key="conversation.id"
        @click="selectConversation(conversation)"
        :class="[
          'tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2.5 tw-rounded-xl tw-cursor-pointer tw-transition-all tw-group tw-mb-0.5',
          props.activeConversationId === conversation.id
            ? 'tw-bg-primary-50 tw-border tw-border-primary-100'
            : 'hover:tw-bg-gray-50 tw-border tw-border-transparent'
        ]"
      >
        <!-- Avatar -->
        <div class="tw-relative tw-flex-shrink-0">
          <!-- Company avatar -->
          <template v-if="conversation.type === 'company'">
            <div class="tw-w-11 tw-h-11 tw-rounded-xl tw-bg-gray-100 tw-border tw-border-gray-200 tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-flex-shrink-0">
              <img
                v-if="conversation.company?.logo"
                :src="conversation.company.logo"
                :alt="conversation.company.name"
                class="tw-w-full tw-h-full tw-object-contain"
              />
              <icon v-else icon="solar:buildings-2-bold-duotone" class="tw-text-lg tw-text-primary-500" />
            </div>
            <div class="tw-absolute -tw-bottom-0.5 -tw-right-0.5 tw-w-4 tw-h-4 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-border-2 tw-border-white">
              <icon icon="solar:buildings-2-bold-duotone" class="tw-text-primary-600" style="font-size:8px" />
            </div>
          </template>

          <!-- User avatar -->
          <template v-else>
            <div class="tw-w-11 tw-h-11 tw-rounded-full tw-overflow-hidden tw-flex-shrink-0 tw-border-2"
              :class="props.activeConversationId === conversation.id ? 'tw-border-primary-200' : 'tw-border-gray-100'"
            >
              <img
                v-if="conversation.other_user?.profile_image"
                :src="conversation.other_user.profile_image"
                :alt="conversation.other_user.name"
                class="tw-w-full tw-h-full tw-object-cover"
              />
              <div v-else class="tw-w-full tw-h-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center">
                <icon icon="solar:user-bold-duotone" class="tw-text-lg tw-text-gray-400" />
              </div>
            </div>
            <!-- Online dot -->
            <div
              v-if="conversation.other_user?.is_active"
              class="tw-absolute tw-bottom-0 tw-right-0 tw-w-3 tw-h-3 tw-bg-emerald-500 tw-rounded-full tw-border-2 tw-border-white"
            ></div>
          </template>
        </div>

        <!-- Content -->
        <div class="tw-flex-1 tw-min-w-0">
          <div class="tw-flex tw-items-baseline tw-justify-between tw-mb-0.5">
            <h3
              class="tw-text-sm tw-font-semibold tw-truncate"
              :class="conversation.unread_count > 0 ? 'tw-text-gray-900' : 'tw-text-gray-800'"
            >
              {{ getConversationDisplayName(conversation) }}
            </h3>
            <span class="tw-text-[11px] tw-text-gray-400 tw-flex-shrink-0 tw-ml-2">
              {{ formatTimestamp(getConversationTimestamp(conversation)) }}
            </span>
          </div>
          <div class="tw-flex tw-items-center tw-justify-between tw-gap-2">
            <p
              class="tw-text-xs tw-truncate tw-flex-1"
              :class="conversation.unread_count > 0 ? 'tw-text-gray-700 tw-font-medium' : 'tw-text-gray-400'"
            >
              <span v-if="getLastMessagePreview(conversation).isCurrentUser" class="tw-text-primary-500">You: </span>
              {{ getLastMessagePreview(conversation).messageContent }}
            </p>
            <span
              v-if="conversation.unread_count > 0"
              class="tw-bg-primary-500 tw-text-white tw-text-[10px] tw-font-bold tw-px-1.5 tw-py-0.5 tw-rounded-full tw-leading-none tw-flex-shrink-0"
            >
              {{ conversation.unread_count > 99 ? '99+' : conversation.unread_count }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Banner -->
    <div v-if="error" class="tw-p-3 tw-bg-red-50 tw-border-t tw-border-red-100 tw-flex tw-items-center tw-justify-between tw-gap-3">
      <p class="tw-text-xs tw-text-red-600">{{ error }}</p>
      <button @click="fetchConversations" class="tw-text-xs tw-text-red-600 tw-font-medium hover:tw-underline tw-flex-shrink-0">Retry</button>
    </div>
  </div>
</template>

<script setup>
import messagingApi from '@/api/messages';
import { ref, onMounted, onUnmounted, defineEmits, defineProps, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores';
import realtimeMessagingService from '@/services/realtime-messaging';

const props = defineProps({
  activeConversationId: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['conversation-selected', 'conversation-updated']);

const router = useRouter();
const authStore = useAuthStore();

// Tabs
const tabs = [
  { type: 'direct',  label: 'Direct',    icon: 'solar:user-bold-duotone' },
  { type: 'company', label: 'Companies', icon: 'solar:buildings-2-bold-duotone' },
];
const activeType = ref('direct');

// Reactive data
const conversations = ref([]);
const loading = ref(false);
const error = ref(null);
const searchQuery = ref('');

// All conversations across both types (for unread badge on tabs)
const allConversations = ref([]);

// Computed
const sortedConversations = computed(() => {
  return [...conversations.value].sort((a, b) => {
    const getTs = (conv) => {
      if (conv.last_message?.created_at) return new Date(conv.last_message.created_at).getTime();
      if (conv.updated_at) return new Date(conv.updated_at).getTime();
      if (conv.created_at) return new Date(conv.created_at).getTime();
      return 0;
    };
    return getTs(b) - getTs(a);
  });
});

const filteredConversations = computed(() => {
  if (!searchQuery.value.trim()) return sortedConversations.value;
  const q = searchQuery.value.toLowerCase();
  return sortedConversations.value.filter(conv => {
    const name = getConversationDisplayName(conv).toLowerCase();
    const preview = getLastMessagePreview(conv).messageContent?.toLowerCase() || '';
    return name.includes(q) || preview.includes(q);
  });
});

const getTabUnreadCount = (type) => {
  return allConversations.value
    .filter(c => c.type === type || (type === 'direct' && c.type !== 'company'))
    .reduce((sum, c) => sum + (c.unread_count || 0), 0);
};

// Methods
const setActiveType = (type) => {
  if (activeType.value === type) return;
  activeType.value = type;
  conversations.value = [];
  searchQuery.value = '';
  fetchConversations();
};

const getConversationDisplayName = (conversation) => {
  if (conversation.type === 'company') {
    return conversation.company?.name || conversation.name || 'Company Chat';
  }
  return conversation.other_user?.name || 'Unknown User';
};

const fetchConversations = async () => {
  loading.value = true;
  error.value = null;
  try {
    const response = await messagingApi.conversations.paginate({ type: activeType.value });
    if (response.data?.success && response.data?.data) {
      conversations.value = response.data.data;
      // Merge into allConversations for tab badge counts
      const incoming = response.data.data;
      incoming.forEach(c => {
        const idx = allConversations.value.findIndex(x => x.id === c.id);
        if (idx !== -1) allConversations.value[idx] = c;
        else allConversations.value.push(c);
      });
    } else {
      throw new Error('Invalid response format');
    }
  } catch (err) {
    console.error('Error fetching conversations:', err);
    error.value = err.response?.data?.message || 'Failed to load conversations';
  } finally {
    loading.value = false;
  }
};

const refreshConversations = () => fetchConversations();

const selectConversation = (conversation) => {
  router.push({ name: 'shared.messages', params: { id: conversation.id } });
  emit('conversation-selected', conversation);
  if (conversation.unread_count > 0) markAsRead(conversation.id);
};

const markAsRead = async (conversationId) => {
  try {
    await messagingApi.conversations.markAsRead(conversationId);
    const conv = conversations.value.find(c => c.id === conversationId);
    if (conv) {
      conv.unread_count = 0;
      emit('conversation-updated', conv);
    }
    const allConv = allConversations.value.find(c => c.id === conversationId);
    if (allConv) allConv.unread_count = 0;
  } catch (err) {
    console.error('Error marking conversation as read:', err);
  }
};

const getConversationTimestamp = (conversation) => {
  return conversation.last_message?.created_at || conversation.updated_at || conversation.created_at || null;
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffH = (now - date) / (1000 * 60 * 60);
    if (diffH < 24) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    } else if (diffH < 48) {
      return 'Yesterday';
    } else if (diffH < 168) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  } catch { return ''; }
};

const isCurrentUser = (senderId) => authStore.user?.id === senderId;

const getLastMessagePreview = (conversation) => {
  if (conversation.last_message) {
    const currentUser = authStore.user;
    const isFromCurrentUser =
      conversation.last_message.sender_name === currentUser?.name ||
      conversation.last_message.sender_id === currentUser?.id;
    const content = conversation.last_message.content || '...';
    return { messageContent: content, isCurrentUser: isFromCurrentUser };
  }
  if (conversation.messages?.length > 0) {
    const last = conversation.messages[conversation.messages.length - 1];
    return { messageContent: last.content || '...', isCurrentUser: isCurrentUser(last.sender_id) };
  }
  return { messageContent: 'No messages yet', isCurrentUser: false };
};

// Real-time
const setupGlobalMessages = async () => {
  try {
    await realtimeMessagingService.joinGlobalMessages();
    realtimeMessagingService.on('global-conversation-updated', updateConversationInList);
    realtimeMessagingService.on('new-conversation', addConversationToList);
    realtimeMessagingService.on('unread-count-updated', ({ conversation_id, unread_count }) => {
      updateUnreadCount(conversation_id, unread_count);
    });
  } catch (err) {
    console.error('Error setting up global messages:', err);
  }
};

const updateConversationInList = (updated) => {
  const idx = conversations.value.findIndex(c => c.id === updated.id);
  if (idx !== -1) {
    conversations.value[idx] = { ...conversations.value[idx], ...updated };
    emit('conversation-updated', updated);
  }
  const allIdx = allConversations.value.findIndex(c => c.id === updated.id);
  if (allIdx !== -1) allConversations.value[allIdx] = { ...allConversations.value[allIdx], ...updated };
};

const addConversationToList = (newConv) => {
  if (!conversations.value.find(c => c.id === newConv.id)) {
    conversations.value.unshift(newConv);
  }
  if (!allConversations.value.find(c => c.id === newConv.id)) {
    allConversations.value.unshift(newConv);
  }
};

const updateUnreadCount = (conversationId, unreadCount) => {
  const conv = conversations.value.find(c => c.id === conversationId);
  if (conv) { conv.unread_count = unreadCount; emit('conversation-updated', conv); }
  const allConv = allConversations.value.find(c => c.id === conversationId);
  if (allConv) allConv.unread_count = unreadCount;
};

const cleanupGlobalMessages = () => {
  realtimeMessagingService.leaveGlobalMessages();
  realtimeMessagingService.off('global-conversation-updated');
  realtimeMessagingService.off('new-conversation');
  realtimeMessagingService.off('unread-count-updated');
};

onMounted(async () => {
  await fetchConversations();
  await setupGlobalMessages();
});

onUnmounted(() => cleanupGlobalMessages());
</script>

<style scoped>
.conversations-scroll::-webkit-scrollbar { width: 3px; }
.conversations-scroll::-webkit-scrollbar-track { background: transparent; }
.conversations-scroll::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 4px; }
.conversations-scroll::-webkit-scrollbar-thumb:hover { background: #d1d5db; }
</style>
